import { Button, CodeBox, Loading } from "@alch/ui";
import { ArrowUpRightSm, File05 } from "@alch/ui/icons/16";
import type { Control } from "react-hook-form";
import { useFormState, useWatch } from "react-hook-form";

import type { NetworkInfo } from "@alch/dx-entities";
import { AlchemyDocsUrl } from "@util/urls";

import ComposerShareButton from "@features/composer/ComposerShareButton";
import ComposerRequestPreview from "./ComposerRequestPreview.js";
import type { ComposerFormState } from "./lib/form.js";
import type { ComposerMethodConfig } from "./lib/methods.js";
import type useMakeComposerRequest from "./useMakeComposerRequest.js";

interface ComposerRequestProps extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<ComposerFormState>;
  baseUrl: string;
  methodConfig?: ComposerMethodConfig;
  networkInfo?: NetworkInfo;
  mutation: ReturnType<typeof useMakeComposerRequest>;
}

const ComposerRequest = ({
  control,
  baseUrl,
  methodConfig,
  networkInfo,
  mutation,
  ...props
}: ComposerRequestProps) => {
  const method = useWatch({ control, name: "method" });
  const methodFormState = useFormState({ control, name: "method" });

  // Form state will reset when changing method.
  // State will be out of sync for one render when changing method manually.
  const isChangingMethod = methodFormState.defaultValues?.method !== method;

  if (!methodConfig || isChangingMethod) {
    return (
      <div {...props}>
        <Loading className="mx-auto" />
      </div>
    );
  }

  const responseSizeInMbs = (mutation.data?.body.length || 0) / 10e5;
  const responseIsTooLargeToShow = responseSizeInMbs > 3; // an arbitrary number
  const responseSizeInMbsFormatted = responseSizeInMbs.toLocaleString("us-en", {
    maximumFractionDigits: 0,
  });

  return (
    <div {...props}>
      <ComposerRequestPreview
        control={control}
        methodConfig={methodConfig}
        networkInfo={networkInfo}
      />

      <div className="mt-6 flex flex-wrap gap-2">
        <Button
          as="a"
          href={methodConfig.docsUrl || AlchemyDocsUrl.Home}
          target="_blank"
          intent="tertiary"
          className="mr-auto"
          leadingIcon={<File05 />}
          trailingIcon={<ArrowUpRightSm />}
        >
          View docs
        </Button>

        <ComposerShareButton control={control} baseUrl={baseUrl} />

        <Button type="submit" loading={mutation.isPending}>
          Send request
        </Button>
      </div>

      {mutation.isError ? (
        <CodeBox
          className="mt-6 min-w-0"
          title="Error"
          snippets={[
            {
              language: "json",
              code: mutation.error?.message || "Unknown error",
            },
          ]}
        />
      ) : mutation.data ? (
        <CodeBox
          className="mt-6 min-w-0"
          title="Response"
          snippets={[
            {
              language: responseIsTooLargeToShow ? "text" : "json",
              code: responseIsTooLargeToShow
                ? `Response is too large to show (${responseSizeInMbsFormatted} MB)`
                : mutation.data.body,
            },
          ]}
        />
      ) : null}
    </div>
  );
};

export default ComposerRequest;
